import {
  Button,
  Card,
  DownloadFromCloudButton,
  ErrorAlert,
  LoadingAlert,
  WarningAlert,
} from '@/components';
import TransactionsList, {
  InformationAmount,
} from '@/components/organisms/TransactionsList/TransactionsList';
import { useTransactions } from '@/components/providers/postauth/UserTransactionsProvider';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import APP_CONFIG from '@/config/app';
import { TokenInterface } from '@/interfaces';
import { URLS } from '@/utils/constants';
import { logError, logInfo, logWarning } from '@/utils/logger';
import { TRANSACTIONS, WALLET } from '@/utils/strings';
import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
const { MAIN_GAP } = APP_CONFIG.STYLES;
const spacingConfig = {
  buttonSpacing: `mr${MAIN_GAP} -mt-1`,
};
interface Props {
  className?: string;
  amountOfInformationToShow?: InformationAmount;
  yieldBearingAssetTicker?: string;
  tokens?: TokenInterface[];
  cardTitle?: string;
}
const MostRecentTransactionsCard: FC<Props> = ({
  className,
  amountOfInformationToShow = InformationAmount.LEAST_INFORMATION,
  yieldBearingAssetTicker,
  tokens,
  cardTitle = 'Recent Transactions',
}) => {
  const router = useRouter();
  const {
    isLoading: loading,
    isInitialLoading: initialWalletsLoading,
    error: walletsError,
    isUserAuthorized,
    refreshWalletsAndTransactions,
    isWalletAndTransactionRefreshing: isWalletAndTransactionRefreshInProgress,
  } = useWalletsContext();
  const {
    isInitialLoading: initialTransactionsLoading,
    error: transactionsError,
    transactions,
  } = useTransactions();
  const [lastSuccessfulState, setLastSuccessfulState] =
    useState<JSX.Element | null>(null);
  useEffect(() => {
    if (yieldBearingAssetTicker) {
      setLastSuccessfulState(null);
    }
  }, [yieldBearingAssetTicker]);
  const handleSeeAllTransactions = useCallback(() => {
    router.push(URLS.allTransactions);
  }, [router]);
  const handleRefreshWalletsAndTransactions = useCallback(async () => {
    try {
      logInfo(
        'Refreshing wallets and transactions...',
        'MostRecentTransactionsCard.handleRefreshWalletsAndTransactions',
      );
      await refreshWalletsAndTransactions();
      logInfo(
        'Wallets and transactions refresh complete',
        'MostRecentTransactionsCard.handleRefreshWalletsAndTransactions',
      );
    } catch (error) {
      logError(
        error,
        'MostRecentTransactionsCard.handleRefreshWalletsAndTransactions',
      );
    }
  }, [refreshWalletsAndTransactions]);
  const isYieldBearingAssetDetailPage = useMemo(() => {
    return router.pathname.startsWith(`${URLS.allYieldBearingAssets}`);
  }, [router.pathname]);
  const isTradePage = useMemo(() => {
    return router.pathname.startsWith(`${URLS.trade}`);
  }, [router.pathname]);
  const renderBodyContent = useMemo(() => {
    if (initialWalletsLoading || initialTransactionsLoading) {
      logInfo(
        'Initial loading in progress for recent transactions',
        'MostRecentTransactionsCard',
      );
      return (
        <LoadingAlert
          title={TRANSACTIONS.LOADING_TITLE}
          description={TRANSACTIONS.LOADING_DESCRIPTION}
        />
      );
    }
    if (!isUserAuthorized) {
      logError(
        'User is not authorized to view transactions',
        'MostRecentTransactionsCard',
      );
      return (
        <ErrorAlert
          title={WALLET.UNAUTHORIZED_USER_ERROR_TITLE}
          description={WALLET.UNAUTHORIZED_USER_ERROR_DESCRIPTION}
        />
      );
    }
    if (walletsError || transactionsError) {
      logError('Error loading transactions', 'MostRecentTransactionsCard', {
        walletsError,
        transactionsError,
      });
      return (
        <WarningAlert
          title={TRANSACTIONS.GENERIC_ERROR_LOADING_TITLE}
          description={TRANSACTIONS.GENERIC_ERROR_LOADING_DESCRIPTION}
        />
      );
    }
    if (!loading && !transactions.length) {
      logWarning('No transactions found', 'MostRecentTransactionsCard');
      return (
        <WarningAlert
          title={TRANSACTIONS.TRANSACTIONS_NOT_FOUND_TITLE}
          description={TRANSACTIONS.TRANSACTIONS_NOT_FOUND_DESCRIPTION}
        />
      );
    }
    return (
      <TransactionsList
        key={yieldBearingAssetTicker}
        filters={{
          ...(!yieldBearingAssetTicker &&
            (!tokens || tokens.length === 0) && {
              limit:
                APP_CONFIG.MOST_RECENT_TRANSACTIONS_CARD
                  .NO_OF_TRANSACTIONS_TO_SHOW,
            }),
          ...(yieldBearingAssetTicker && {
            yieldBearingAssetId: yieldBearingAssetTicker,
          }),
          ...(tokens && tokens.length > 0 && { tokens: tokens }),
        }}
        amountOfInformationToShow={amountOfInformationToShow}
      />
    );
  }, [
    initialWalletsLoading,
    isUserAuthorized,
    walletsError,
    transactionsError,
    transactions,
    loading,
    amountOfInformationToShow,
    yieldBearingAssetTicker,
    tokens,
  ]);
  useEffect(() => {
    if (
      !initialWalletsLoading &&
      !loading &&
      !(renderBodyContent?.type === LoadingAlert)
    ) {
      setLastSuccessfulState(renderBodyContent);
    }
  }, [renderBodyContent, initialWalletsLoading, loading]);
  const shouldShowRefreshInProgressIndicator = useMemo(
    () => !initialWalletsLoading && isWalletAndTransactionRefreshInProgress,
    [initialWalletsLoading, isWalletAndTransactionRefreshInProgress],
  );
  const shouldShowRefreshWalletsAndTransactionsButton = useMemo(
    () => !initialWalletsLoading && !initialTransactionsLoading,
    [initialWalletsLoading, initialTransactionsLoading],
  );
  const shouldDisableRefreshWalletsAndTransactionsButton = useMemo(
    () => isWalletAndTransactionRefreshInProgress,
    [isWalletAndTransactionRefreshInProgress],
  );
  return (
    <Card className={className}>
      <Card.Header
        title={cardTitle}
        loading={shouldShowRefreshInProgressIndicator}
        loadingIsButton={false}
        leftNodeElements={[]}
        rightNodeElements={[
          shouldShowRefreshWalletsAndTransactionsButton && (
            <DownloadFromCloudButton
              key="refresh_wallets_and_transactions_button"
              onDownloadClick={handleRefreshWalletsAndTransactions}
              intent="ghost"
              className={spacingConfig.buttonSpacing}
              disabled={shouldDisableRefreshWalletsAndTransactionsButton}
            />
          ),
          <Button
            intent="ghost"
            key="see_all_transactions_from_recent_transactions_button"
            onClick={handleSeeAllTransactions}
          >
            See all
          </Button>,
        ].filter(Boolean)}
      />
      <Card.Body>
        {loading && !initialWalletsLoading && lastSuccessfulState
          ? lastSuccessfulState
          : renderBodyContent}
      </Card.Body>
    </Card>
  );
};
export default MostRecentTransactionsCard;
